const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024

const CLOUDINARY_CONFIG = {
  cloudName: (process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "").trim(),
  uploadPreset: (process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "").trim(),
  uploadFolder: (
    process.env.REACT_APP_CLOUDINARY_UPLOAD_FOLDER || "service-orders"
  ).trim(),
}

const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
])

const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
])

export const SERVICE_ORDER_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

const getFileExtension = (fileName) => {
  if (typeof fileName !== "string") {
    return ""
  }

  const dotIndex = fileName.lastIndexOf(".")
  if (dotIndex === -1) {
    return ""
  }

  return fileName.slice(dotIndex).toLowerCase()
}

const sanitizePathSegment = (value) => {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
}

const sanitizeFileName = (fileName) => {
  const safeName = sanitizePathSegment(fileName)
  if (safeName) {
    return safeName
  }

  return `document-${Date.now()}`
}

const stripFileExtension = (fileName) => {
  const extension = getFileExtension(fileName)
  if (!extension) {
    return fileName
  }

  return fileName.slice(0, -extension.length)
}

const isPdfFile = (file) => {
  const extension = getFileExtension(file?.name || "")
  const mimeType = (file?.type || "").toLowerCase()

  return extension === ".pdf" || mimeType === "application/pdf"
}

export const validateServiceOrderAttachment = (file) => {
  if (!file) {
    return null
  }

  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    return "Le fichier dépasse la taille maximale autorisée (10 Mo)."
  }

  const hasAllowedMimeType = ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)
  const hasAllowedExtension = ALLOWED_ATTACHMENT_EXTENSIONS.has(
    getFileExtension(file.name)
  )

  if (!hasAllowedMimeType && !hasAllowedExtension) {
    return "Format non pris en charge. Utilisez un PDF, Word ou Excel."
  }

  return null
}

export const uploadServiceOrderAttachment = async ({
  file,
  serviceId,
  customerEmail,
}) => {
  if (!CLOUDINARY_CONFIG.cloudName || !CLOUDINARY_CONFIG.uploadPreset) {
    const configError = new Error(
      "Upload document indisponible: configuration Cloudinary incomplète."
    )
    configError.code = "cloudinary/config-missing"
    throw configError
  }

  const safeServiceId = sanitizePathSegment(serviceId) || "service"
  const safeEmail = sanitizePathSegment(customerEmail) || "client"
  const fileName = sanitizeFileName(file.name)
  const publicIdBase = stripFileExtension(fileName) || `document-${Date.now()}`
  const folder = [CLOUDINARY_CONFIG.uploadFolder, safeServiceId, safeEmail]
    .filter(Boolean)
    .join("/")
  const publicId = `${Date.now()}-${publicIdBase}`
  const resourceType = isPdfFile(file) ? "image" : "raw"

  const uploadEndpoint = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CONFIG.cloudName}/${resourceType}/upload`
  const formData = new FormData()
  formData.append("file", file)
  formData.append("upload_preset", CLOUDINARY_CONFIG.uploadPreset)
  formData.append("folder", folder)
  formData.append("public_id", publicId)

  let uploadResponse
  let uploadPayload

  try {
    uploadResponse = await fetch(uploadEndpoint, {
      method: "POST",
      body: formData,
    })

    uploadPayload = await uploadResponse.json()

    if (!uploadResponse.ok) {
      const cloudinaryMessage =
        uploadPayload?.error?.message || "Cloudinary upload failed"

      const uploadError = new Error(cloudinaryMessage)
      uploadError.code = `cloudinary/http-${uploadResponse.status}`
      uploadError.originalError = uploadPayload
      throw uploadError
    }
  } catch (error) {
    const uploadError = new Error(
      getServiceOrderAttachmentUploadErrorMessage(error)
    )
    uploadError.code = error?.code || "attachment/upload-failed"
    uploadError.originalError = error
    throw uploadError
  }

  return {
    name: file.name,
    size: file.size,
    contentType: file.type || "application/octet-stream",
    resourceType,
    storagePath: uploadPayload?.public_id || `${folder}/${publicId}`,
    downloadUrl: uploadPayload?.secure_url || "",
  }
}

export const getServiceOrderAttachmentUploadErrorMessage = (error) => {
  const errorCode = error?.code

  if (errorCode === "cloudinary/config-missing") {
    return "Le document n'a pas pu être envoyé: configuration Cloudinary manquante (cloud name / upload preset)."
  }

  if (errorCode === "cloudinary/http-400") {
    return "Le document n'a pas pu être envoyé: configuration Cloudinary invalide (preset, format ou paramètres)."
  }

  if (errorCode === "cloudinary/http-401") {
    return "Le document n'a pas pu être envoyé: authentification Cloudinary refusée. Vérifiez l'upload preset."
  }

  if (errorCode === "cloudinary/http-403") {
    return "Le document n'a pas pu être envoyé: accès Cloudinary refusé pour cet upload preset."
  }

  if (errorCode === "cloudinary/http-413") {
    return "Le document n'a pas pu être envoyé: taille trop importante pour la configuration Cloudinary."
  }

  if (errorCode === "cloudinary/http-429") {
    return "Le document n'a pas pu être envoyé: trop de requêtes, merci de réessayer dans quelques instants."
  }

  if (errorCode === "cloudinary/delivery-blocked") {
    return "Le document a été importé mais n'est pas accessible publiquement (Cloudinary: Blocked for delivery). Passez l'asset/preset en accès public, puis réessayez."
  }

  if (errorCode === "cloudinary/show_original_customer_untrusted") {
    return "Le document est importé, mais votre compte Cloudinary est marqué 'untrusted' pour la livraison du fichier original. Contactez le support Cloudinary pour lever la restriction."
  }

  if (errorCode === "attachment/upload-failed") {
    return "Le document n'a pas pu être envoyé. Vérifiez votre connexion et réessayez."
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return `Le document n'a pas pu être envoyé: ${error.message.trim()}`
  }

  return "Le document n'a pas pu être envoyé. Vérifiez votre connexion et réessayez."
}
